import { useState, useEffect } from 'react';
import { X, UserPlus, UserCog, Plus, Loader2, Briefcase, Info } from 'lucide-react';
import { supabase } from '../lib/supabase';
import bcrypt from 'bcryptjs';

interface Agency {
  name: string;
}

interface User {
  id: string;
  email: string;
  firstname: string;
  lastname: string;
  agency: string;
  role: string;
  fonction?: string | null;
  email_notifications_enabled?: boolean;
  email_2fa_enabled?: boolean;
  totp_enabled?: boolean;
}

interface UserModalProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const NEG_ROLES    = ['négociateur', 'directeur', 'responsable', 'animateur'] as const;
const NEG_STATUSES = ['conseiller', 'confirmé', 'senior', 'expert', 'animateur'] as const;

const FONCTION_LABELS: Record<string, string> = {
  '':           'Aucune (admin / accès seul)',
  negociateur:  'Négociateur / Directeur',
  assistante:   'Assistante',
};

export default function UserModal({ user, isOpen, onClose, onSuccess }: UserModalProps) {
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const [formData, setFormData] = useState({
    agencies:                    [{ name: '' }],
    email:                       '',
    firstname:                   '',
    lastname:                    '',
    role:                        'user',
    password:                    '',
    email_notifications_enabled: true,
    email_2fa_enabled:           false,
    // Fonction
    fonction:    '' as '' | 'negociateur' | 'assistante',
    neg_role:    'négociateur' as typeof NEG_ROLES[number],
    neg_status:  'conseiller'  as typeof NEG_STATUSES[number],
    arrival_date: '',
  });

  useEffect(() => { loadAgencies(); }, []);

  useEffect(() => {
    if (user) {
      const userAgencies = user.agency
        ? user.agency.split(',').map(a => ({ name: a.trim() }))
        : [{ name: '' }];
      setFormData({
        agencies:                    userAgencies,
        email:                       user.email     || '',
        firstname:                   user.firstname || '',
        lastname:                    user.lastname  || '',
        role:                        user.role      || 'user',
        password:                    '',
        email_notifications_enabled: user.email_notifications_enabled ?? true,
        email_2fa_enabled:           user.email_2fa_enabled           ?? false,
        fonction:    (user.fonction as any) || '',
        neg_role:    'négociateur',
        neg_status:  'conseiller',
        arrival_date: '',
      });
    } else {
      setFormData({
        agencies: [{ name: '' }],
        email: '', firstname: '', lastname: '',
        role: 'user', password: '',
        email_notifications_enabled: true,
        email_2fa_enabled: false,
        fonction: '', neg_role: 'négociateur', neg_status: 'conseiller', arrival_date: '',
      });
    }
  }, [user]);

  const loadAgencies = async () => {
    try {
      const { data } = await supabase.from('agencies').select('name').eq('active', true).order('name');
      setAgencies(data || []);
    } catch (err) {
      console.error('Error loading agencies:', err);
    }
  };

  const handleAddAgency    = () => setFormData(p => ({ ...p, agencies: [...p.agencies, { name: '' }] }));
  const handleRemoveAgency = (i: number) => setFormData(p => ({ ...p, agencies: p.agencies.filter((_, idx) => idx !== i) }));
  const handleAgencyChange = (i: number, v: string) =>
    setFormData(p => ({ ...p, agencies: p.agencies.map((a, idx) => idx === i ? { name: v } : a) }));

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const isReadOnlyRole = formData.role === 'lecture';

    if (!isReadOnlyRole && formData.agencies.some(a => !a.name)) {
      setError('Toutes les agences doivent être sélectionnées');
      setLoading(false);
      return;
    }
    const uniqueAg = new Set(formData.agencies.map(a => a.name));
    if (uniqueAg.size !== formData.agencies.length) {
      setError('Une agence ne peut pas être sélectionnée plusieurs fois');
      setLoading(false);
      return;
    }

    try {
      // Le profil lecture seule n'est rattaché à aucune agence
      const agencyStr = isReadOnlyRole ? '' : formData.agencies.map(a => a.name).join(', ');

      let updateData: any = {
        email:     formData.email,
        firstname: formData.firstname,
        lastname:  formData.lastname,
        agency:    agencyStr,
        role:      formData.role,
        fonction:  formData.fonction || null,
        email_notifications_enabled: formData.email_notifications_enabled,
        email_2fa_enabled:           formData.email_2fa_enabled,
      };

      if (formData.password) {
        const salt = await bcrypt.genSalt(10);
        updateData.password = await bcrypt.hash(formData.password, salt);
      }

      if (user) {
        // ── Edit mode: update user only ───────────────────────────────────
        const { error: upErr } = await supabase.from('users').update(updateData).eq('id', user.id);
        if (upErr) throw new Error('Erreur lors de la mise à jour');
      } else {
        // ── Create mode ───────────────────────────────────────────────────
        if (!formData.password) throw new Error('Le mot de passe est requis pour la création');

        const { error: crErr } = await supabase.from('users').insert([updateData]);
        if (crErr) throw new Error('Erreur lors de la création de l\'utilisateur');

        const validAgencies = formData.agencies.filter(a => a.name);

        // Auto-créer la fiche négociateur
        if (formData.fonction === 'negociateur') {
          const { data: neg, error: negErr } = await supabase
            .from('negotiators')
            .insert([{
              firstname:    formData.firstname,
              lastname:     formData.lastname.toUpperCase(),
              role:         formData.neg_role,
              status:       formData.neg_status,
              arrival_date: formData.arrival_date || null,
              archived:     false,
            }])
            .select('id')
            .single();

          if (negErr) {
            throw new Error(`Utilisateur créé mais erreur fiche négociateur : ${negErr.message}`);
          }

          if (neg) {
            // Lier l'utilisateur à sa fiche négociateur
            await supabase
              .from('users')
              .update({ linked_negotiator_id: neg.id })
              .eq('email', formData.email.trim().toLowerCase());

            if (validAgencies.length > 0) {
              await supabase.from('negotiator_agencies').insert(
                validAgencies.map(ag => ({
                  negotiator_id:        neg.id,
                  agency:               ag.name,
                  objective_percentage: 100,
                }))
              );
            }
          }
        }

        // Auto-créer la fiche assistante
        if (formData.fonction === 'assistante') {
          const { data: assi, error: assiErr } = await supabase
            .from('assistants')
            .insert([{
              firstname: formData.firstname,
              lastname:  formData.lastname.toUpperCase(),
              archived:  false,
            }])
            .select('id')
            .single();

          if (assiErr) {
            throw new Error(`Utilisateur créé mais erreur fiche assistante : ${assiErr.message}`);
          }

          if (assi) {
            // Lier l'utilisateur à sa fiche assistante
            await supabase
              .from('users')
              .update({ linked_assistant_id: assi.id })
              .eq('email', formData.email.trim().toLowerCase());

            if (validAgencies.length > 0) {
              await supabase.from('assistant_agencies').insert(
                validAgencies.map(ag => ({
                  assistant_id: assi.id,
                  agency:       ag.name,
                }))
              );
            }
          }
        }
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors bg-white";
  const labelClass = "block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide";
  const selectClass = "w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors bg-white";

  const isEdit = !!user;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg relative max-h-[92vh] flex flex-col shadow-2xl">

        {/* ── Header ── */}
        <div className="relative rounded-t-2xl overflow-hidden bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-950 px-6 py-5 shrink-0">
          <div className="absolute top-0 right-0 w-40 h-40 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
          <div className="relative flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
                {isEdit ? <UserCog size={18} className="text-white/80" /> : <UserPlus size={18} className="text-white/80" />}
              </div>
              <div>
                <h2 className="text-lg font-bold text-white tracking-tight">
                  {isEdit ? "Modifier l'utilisateur" : 'Créer un utilisateur'}
                </h2>
                {isEdit && <p className="text-sm text-slate-300 mt-0.5">{user.firstname} {user.lastname}</p>}
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors shrink-0">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto flex-1 p-6 space-y-4">

            {/* Email */}
            <div>
              <label className={labelClass}>Email</label>
              <input type="email" value={formData.email}
                onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                className={inputClass} placeholder="exemple@domaine.fr" required />
            </div>

            {/* Prénom / Nom */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Prénom</label>
                <input type="text" value={formData.firstname}
                  onChange={e => setFormData(p => ({ ...p, firstname: e.target.value }))}
                  className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Nom</label>
                <input type="text" value={formData.lastname}
                  onChange={e => setFormData(p => ({ ...p, lastname: e.target.value }))}
                  className={inputClass} required />
              </div>
            </div>

            {/* Agences */}
            {formData.role !== 'lecture' && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className={labelClass} style={{ marginBottom: 0 }}>Agences</label>
                <button type="button" onClick={handleAddAgency}
                  className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-700 transition-colors">
                  <Plus size={13} /> Ajouter une agence
                </button>
              </div>
              <div className="space-y-2 mt-1.5">
                {formData.agencies.map((agency, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <select value={agency.name} onChange={e => handleAgencyChange(idx, e.target.value)}
                      className={selectClass} required>
                      <option value="">Sélectionner une agence</option>
                      {agencies.map(ag => <option key={ag.name} value={ag.name}>{ag.name}</option>)}
                    </select>
                    {formData.agencies.length > 1 && (
                      <button type="button" onClick={() => handleRemoveAgency(idx)}
                        className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 transition-colors shrink-0">
                        <X size={15} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            )}

            {/* Rôle système */}
            <div>
              <label className={labelClass}>Accès système</label>
              <select value={formData.role} onChange={e => setFormData(p => ({ ...p, role: e.target.value }))}
                className={selectClass} required>
                <option value="user">Utilisateur</option>
                <option value="responsable">Responsable</option>
                <option value="direction">Direction</option>
                <option value="admin">Administrateur</option>
                <option value="lecture">Lecture seule</option>
              </select>
            </div>

            {/* ── Fonction ── */}
            <div className="border border-indigo-100 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 bg-indigo-50 px-4 py-2.5 border-b border-indigo-100">
                <Briefcase size={14} className="text-indigo-500 shrink-0" />
                <span className="text-xs font-bold text-indigo-700 uppercase tracking-wide">Fonction</span>
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <label className={labelClass}>Fonction dans l'entreprise</label>
                  {isEdit ? (
                    /* En édition : lecture seule */
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-2 bg-slate-100 rounded-xl text-sm text-slate-700 font-medium">
                        {FONCTION_LABELS[formData.fonction] ?? 'Aucune'}
                      </span>
                      <span className="flex items-center gap-1 text-[11px] text-slate-400 italic">
                        <Info size={12} /> Non modifiable après création
                      </span>
                    </div>
                  ) : (
                    <select value={formData.fonction}
                      onChange={e => setFormData(p => ({ ...p, fonction: e.target.value as any }))}
                      className={selectClass}>
                      {Object.entries(FONCTION_LABELS).map(([v, l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Champs négociateur — création uniquement */}
                {formData.fonction === 'negociateur' && !isEdit && (
                  <div className="space-y-3 pt-1 border-t border-indigo-100">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelClass}>Type</label>
                        <select value={formData.neg_role}
                          onChange={e => setFormData(p => ({ ...p, neg_role: e.target.value as any }))}
                          className={selectClass}>
                          {NEG_ROLES.map(r => (
                            <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>Statut</label>
                        <select value={formData.neg_status}
                          onChange={e => setFormData(p => ({ ...p, neg_status: e.target.value as any }))}
                          className={selectClass}>
                          {NEG_STATUSES.map(s => (
                            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>Date d'arrivée</label>
                      <input type="date" value={formData.arrival_date}
                        onChange={e => setFormData(p => ({ ...p, arrival_date: e.target.value }))}
                        className={inputClass} />
                    </div>
                    <p className="flex items-start gap-1.5 text-[11px] text-indigo-500 italic">
                      <Info size={12} className="shrink-0 mt-0.5" />
                      Une fiche négociateur sera automatiquement créée. Les objectifs pourront être définis depuis la section Négociateurs.
                    </p>
                  </div>
                )}

                {formData.fonction === 'assistante' && !isEdit && (
                  <p className="flex items-start gap-1.5 text-[11px] text-indigo-500 italic pt-1 border-t border-indigo-100">
                    <Info size={12} className="shrink-0 mt-0.5" />
                    Une fiche assistante sera automatiquement créée avec les mêmes agences.
                  </p>
                )}
              </div>
            </div>

            {/* Mot de passe */}
            <div>
              <label className={labelClass}>{isEdit ? 'Nouveau mot de passe' : 'Mot de passe'}</label>
              {isEdit && <p className="text-xs text-slate-400 mb-1.5">Laisser vide pour ne pas modifier</p>}
              <input type="password" value={formData.password}
                onChange={e => setFormData(p => ({ ...p, password: e.target.value }))}
                className={inputClass} required={!isEdit} />
            </div>

            {/* Notifications */}
            <div className="bg-slate-50 rounded-xl border border-slate-100 px-4 py-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={formData.email_notifications_enabled}
                  onChange={e => setFormData(p => ({ ...p, email_notifications_enabled: e.target.checked }))}
                  className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                <span className="text-sm font-medium text-slate-700">Recevoir les notifications par email</span>
              </label>
            </div>

            {/* 2FA email */}
            <div className="bg-amber-50 rounded-xl border border-amber-100 px-4 py-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={formData.email_2fa_enabled}
                  onChange={e => setFormData(p => ({ ...p, email_2fa_enabled: e.target.checked }))}
                  className="w-4 h-4 mt-0.5 rounded border-amber-300 text-amber-600 focus:ring-amber-500" />
                <div>
                  <span className="text-sm font-semibold text-amber-800">Double authentification par email (2FA)</span>
                  <p className="text-xs text-amber-600 mt-0.5">Un code à 6 chiffres sera envoyé à chaque connexion.</p>
                </div>
              </label>
            </div>

            {error && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">
                {error}
              </div>
            )}
          </div>

          {/* ── Footer ── */}
          <div className="border-t border-slate-100 bg-slate-50/60 px-6 py-4 flex justify-end gap-3 shrink-0 rounded-b-2xl">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors">
              Annuler
            </button>
            <button type="submit" disabled={loading}
              className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-colors shadow-sm disabled:opacity-50">
              {loading && <Loader2 size={14} className="animate-spin" />}
              {loading ? 'Enregistrement…' : (isEdit ? 'Enregistrer' : 'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
